import ResponsiveImage from "./common/ResponsiveImage";
import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Activity, Users, Package, Receipt, UserCircle2,
  LifeBuoy, Wallet, Star, ShieldCheck, ChevronsLeft, ChevronsRight, RefreshCcw, FileUp,
} from "lucide-react";
import { cn } from "../lib/utils";
import { canAccess } from "../lib/permissions";

const NAV = [
  { to: "/", section: "overview", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard", section: "dashboard", label: "Dashboard", icon: Activity },
  { to: "/suppliers", section: "suppliers", label: "Suppliers", icon: Users },
  { to: "/product-import", section: "suppliers", label: "Product Import", icon: FileUp },
  { to: "/experiences", section: "experiences", label: "Experiences", icon: Package },
  { to: "/bookings", section: "bookings", label: "Bookings", icon: Receipt },
  { to: "/customers", section: "customers", label: "Customers", icon: UserCircle2 },
  { to: "/support", section: "support", label: "Support Cases", icon: LifeBuoy },
  { to: "/refund-requests", section: "refunds", label: "Refund Requests", icon: RefreshCcw },
  { to: "/payments", section: "payments", label: "Payments & Payouts", icon: Wallet },
  { to: "/quality", section: "reviews", label: "Quality", icon: Star },
  { to: "/admins", section: "admins", label: "Admin Users", icon: ShieldCheck, superAdminOnly: true },
];

export function Sidebar({ user, collapsed, onToggleCollapsed }) {
  return (
    <aside
      data-testid="sidebar"
      className={cn(
        "border-r border-border bg-card flex flex-col transition-[width] duration-200 ease-out shrink-0",
        collapsed ? "w-[68px]" : "w-64"
      )}
    >
      <div className="h-14 flex items-center justify-between px-4 border-b border-border">
        <Link to="/" className="flex items-center gap-2 group" data-testid="brand-link">
          <ResponsiveImage imageWidth={960} src="/voocly-icon.png" alt="Voocly" className="w-8 h-8 rounded-md object-contain" />
          {!collapsed && (
            <div className="flex flex-col leading-tight">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Admin Portal</span>
            </div>
          )}
        </Link>
        <button
          onClick={onToggleCollapsed}
          className="hidden lg:inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
          aria-label="Toggle sidebar"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {NAV.map((n) => {
          if (n.superAdminOnly && !canAccess(user, "admins")) return null;
          if (!canAccess(user, n.section)) return null;
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.section}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                  isActive
                    ? "bg-accent text-accent-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
                )
              }
            >
              <Icon className="w-4 h-4 shrink-0" strokeWidth={1.6} />
              {!collapsed && <span className="truncate">{n.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-border px-3 py-3">
        {!collapsed ? (
          <div className="text-[11px] text-muted-foreground leading-tight">
            <p className="font-medium text-foreground">Operational status</p>
            <p className="mt-0.5">All systems normal</p>
          </div>
        ) : (
          <div className="w-2 h-2 rounded-full bg-[hsl(var(--status-success-text))] mx-auto" />
        )}
      </div>
    </aside>
  );
}
